import type { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import {
  assertResponseCurrent,
  assertSessionCurrent,
  errorStatus,
  isIdentityToken,
  renewSession,
  prepareSession,
  type SessionSnapshot,
  type SessionTransport,
} from "./sessionRenewal";

type SessionConfig = InternalAxiosRequestConfig & {
  sessionSnapshot?: SessionSnapshot;
  sessionRetried?: boolean;
};

export function isSessionEndpoint(url: string) {
  // Only public credential exchanges, not every /auth route: signup and 2FA
  // enrollment are authenticated commands in the BFF.
  return (
    /\/auth\/(?:local\/signin|verifycode|business|refresh|logout|set-password|2fa\/verify)(?:[/?]|$)/.test(
      url
    ) || /\/act-as(?:[/?]|$)/.test(url)
  );
}

/**
 * Rutas donde un 401 NO es la sesión vencida (trd-client#342). En
 * `POST /auth/change-password` el borde ya validó el token: el 401 es el
 * bloqueo por intentos de identity, y viene sin `code`. Renovar y reintentar
 * mandaría otra vez la misma contraseña, que cuenta como otro intento.
 */
export function isCredentialCheckEndpoint(url: string) {
  return /\/auth\/change-password(?:[/?]|$)/.test(url);
}

/** Install once per HTTP client. The rotation coordinator is shared. */
export function installSessionInterceptors(
  client: AxiosInstance,
  io: SessionTransport
) {
  client.interceptors.request.use(async (config: SessionConfig) => {
    if (isSessionEndpoint(config.url ?? "")) return config;
    // Explicit bearer (picker, act-as handoff, etc.) is caller-owned.
    if (config.headers.has("Authorization") && !config.sessionSnapshot)
      return config;
    const store = await io.loadStore();
    const snapshot = config.sessionSnapshot ?? (await prepareSession(io));
    await assertSessionCurrent(store, snapshot);
    config.sessionSnapshot = snapshot;
    if (snapshot.token)
      config.headers.Authorization = `Bearer ${snapshot.token}`;
    if (snapshot.businessId) config.headers.businessId = snapshot.businessId;
    return config;
  });
  client.interceptors.response.use(
    async (response) => {
      const snapshot = (response.config as SessionConfig).sessionSnapshot;
      if (snapshot)
        await assertResponseCurrent(
          await io.loadStore(),
          snapshot,
          response.config.url ?? ""
        );
      return response;
    },
    async (error) => {
      const config = error.config as SessionConfig | undefined;
      const snapshot = config?.sessionSnapshot;
      if (
        !config ||
        !snapshot ||
        config.sessionRetried ||
        errorStatus(error) !== 401 ||
        isCredentialCheckEndpoint(config.url ?? "") ||
        !isIdentityToken(snapshot.token)
      )
        throw error;
      const next = await renewSession(io, snapshot);
      config.sessionSnapshot = next;
      config.sessionRetried = true;
      config.headers.Authorization = `Bearer ${next.token}`;
      return client(config);
    }
  );
}
